import { Injectable, ChangeDetectorRef } from '@angular/core';

/**
 * Service that optimizes change detection calls by ensuring that `detectChanges()`
 * is only executed once per execution cycle, even if `update()` is called multiple times.
 *
 * This service is useful for preventing unnecessary re-rendering of Angular components,
 * improving performance and efficiency.
 *
 * @example
 * // Providing ViewRenderer in a Component
 * import { Component, ChangeDetectorRef } from '@angular/core';
 * import { ViewRenderer } from '../services/view-renderer.service';
 *
 * @Component({
 *   selector: 'app-example',
 *   template: `<button (click)="increment()">Increment</button><p>{{ counter }}</p>`,
 *   providers: [
 *     {
 *       provide: ViewRenderer,
 *       useFactory: (cdRef: ChangeDetectorRef) => new ViewRenderer(cdRef),
 *       deps: [ChangeDetectorRef]
 *     }
 *   ]
 * })
 * export class ExampleComponent {
 *   counter = 0;
 *
 *   constructor(private renderer: ViewRenderer) {} // Inject ViewRenderer with ChangeDetectorRef
 *
 *   increment() {
 *     for (let i = 0; i < 10; i++) {
 *       this.counter++;
 *       this.renderer.markForCheck(); // `detectChanges()` will only be called once per cycle
 *     }
 *   }
 * }
 */
@Injectable()
export class ViewRenderer {
    private _scheduled = false;
    private _isongoing = false;

    /**
     * Constructs the `ViewRenderer` service and injects the `ChangeDetectorRef`.
     *
     * @param change - The `ChangeDetectorRef` instance for managing change detection in Angular.
     */
    constructor(private change: ChangeDetectorRef) {
        // nothing to do
    }

    /**
     * Returns the `ChangeDetectorRef` instance associated with this service.
     *
     * @returns The `ChangeDetectorRef` instance used for change detection.
     *
     * @example
     * // Accessing ChangeDetectorRef from ViewRenderer
     * const cdRef = this.renderer.cdref;
     * cdRef.markForCheck();
     */
    get cdref(): ChangeDetectorRef{
        return this.change;
    }

    /**
     * Schedules a change detection cycle. If called multiple times in the same execution cycle,
     * it ensures that `detectChanges()` is only executed once.
     *
     * `force=true` runs `detectChanges()` synchronously before returning, regardless of a
     * pending deferred cycle: callers that need the DOM updated before reading it back (e.g.
     * `@ViewChild` right after toggling an `*ngIf`) must be able to rely on this, since Angular's
     * `detectChanges()` is itself synchronous and needs no further signal to know the view is
     * current. The one exception is a **reentrant** `force` call — i.e. one made from code that
     * is itself running as part of this same instance's `detectChanges()` (typically a getter
     * read by this view's own template, e.g. `*ngIf="SomeGetter as x"`, that lazily builds state
     * and forces a refresh before returning). Running `detectChanges()` again on a view that
     * Angular is still in the middle of checking corrupts Ivy's internal change-detection state
     * for the whole app, not just this component, so that case is ignored: the state change it
     * would have rendered is already visible to the in-progress pass, or to the next check.
     */
    markForCheck(force = false): void {
        if (force){
            if (this._isongoing){
                return;
            }
            
            this._scheduled = true;
            this._RunDetectChanges();
            return;
        }

        if (!this._scheduled) {
            this._scheduled = true;
            Promise.resolve().then(() => this._RunDetectChanges()); // delay change detection
        }
    }

    /**
     * Runs `detectChanges()` and always clears the scheduling/ongoing flags, even if the check
     * throws. Without the `finally`, a single failing render would leave `_scheduled` (or
     * `_isongoing`) stuck forever, silently disabling every future `markForCheck()` call on this
     * instance.
     */
    private _RunDetectChanges(): void {
        this._isongoing = true;
        
        try {
            this.change.detectChanges();
        }
        catch (err) {
            console.error('[VIEW RENDERER] detectChanges() failed', err);
        }
        finally {
            this._scheduled = false;
            this._isongoing = false;
        }
    }
}
